/**
 * The Control Center's Connected modules section (tracker #202): the
 * catalogue of code-registered embedded modules, the three lifecycle states
 * and what each one really does, the two switches and the reasoned
 * confirmations behind them, and every way a change can be refused.
 *
 * The state words are deliberately blunt about CONSEQUENCES rather than about
 * mechanism: an administrator deciding to hide a module needs to know that
 * stored data stays and that patient badges go, not which gate refuses which
 * request class. A module's own name is server-published and never appears
 * here; the sentences hold a `{name}` slot for it.
 */
export const controlCenterModules = {
  "clinicModules.title": "Clinic modules",
  'clinicModules.subtitle':
    'Embedded modules registered in this installation, and whether each one is on, off or hidden in this clinic.',
  'clinicModules.loading': 'Loading connected modules',
  'clinicModules.loadFailed': 'The module catalogue could not be loaded.',
  'clinicModules.loadForbidden': 'This account may not view the module catalogue.',
  'clinicModules.forbidden': 'You may not view the module catalogue for this clinic',
  'clinicModules.listLabel': 'Registered modules',
  'clinicModules.empty': 'No modules are available',
  'clinicModules.emptyDetail':
    'No modules are available for this organization.',
  'clinicModules.readOnly': 'You can see the state of each module but not change it.',
  'clinicModules.readOnlyItem': 'The server does not allow this account to change this module.',
  'clinicModules.column.module': 'Module',
  'clinicModules.column.controls': 'Availability',
  'clinicModules.backToModules': 'Back to Modules',
  'clinicModules.notFoundTitle': 'Module not found',
  'clinicModules.notFound': 'This module is unavailable for this organization.',

  'clinicModules.views.label': '{name} sections',
  'clinicModules.views.title': 'Title sheet',
  'clinicModules.views.titleDescription': 'Identity, status and module boundaries',
  'clinicModules.views.settings': 'Settings',
  'clinicModules.views.settingsDescription': 'Availability and integration configuration',
  'clinicModules.views.debug': 'Debug',
  'clinicModules.views.debugDescription': 'Prepared interface checks',
  'clinicModules.titleSheet.subtitle': 'Registered module information',
  'clinicModules.titleSheet.identity': 'Module identity',
  'clinicModules.titleSheet.lifecycle': 'Lifecycle',
  'clinicModules.titleSheet.effect': 'Effect',
  'clinicModules.titleSheet.boundaries': 'Dependencies and outbound effects',
  'clinicModules.settings.title': '{name} settings',
  'clinicModules.settings.subtitle': 'Clinic-wide module availability and integration configuration.',
  'clinicModules.settings.lifecycle': 'Availability',
  'clinicModules.debug.unavailableTitle': 'Debug is unavailable',
  'clinicModules.debug.unavailable': 'This build or account does not offer debug actions for this module.',

  'clinicModules.code': 'Module code',
  'clinicModules.version': 'Version',
  'clinicModules.build': 'Build',
  'clinicModules.dependsOn': 'Uses Core services',
  'clinicModules.dependsOnNone': 'None declared',
  'clinicModules.egress': 'Outbound effects',
  'clinicModules.egressNone': 'None declared',
  'clinicModules.egress.ai': 'AI',
  'clinicModules.egress.communications': 'Communications',
  'clinicModules.egress.patient_message': 'Patient messages',
  'clinicModules.egress.outbound_http': 'Outbound HTTP',
  'clinicModules.egress.notification': 'Notifications',
  'clinicModules.egress.background_dispatch': 'Background jobs',
  'clinicModules.lastChange': 'Last change',
  'clinicModules.lastChangeNever': 'Never changed in this clinic',
  'clinicModules.lastChangeBy.account': '{when}, by an account',
  'clinicModules.lastChangeBy.integration': '{when}, by an integration',
  'clinicModules.lastChangeBy.unrecognized': '{when}',
  'clinicModules.changedWhen': 'Changed',

  'clinicModules.status': 'State',
  'clinicModules.status.enabled': 'Enabled',
  'clinicModules.status.disabled': 'Disabled',
  'clinicModules.status.hidden': 'Hidden',
  'clinicModules.statusDetail.enabled':
    'Fully active and visible. Its inbound requests, commands, background work and outbound calls run.',
  'clinicModules.statusDetail.disabled':
    'Visible but stopped. Stored module data stays readable; no new inbound request, command, background job or outbound call runs.',
  'clinicModules.statusDetail.hidden':
    'Stopped and removed from every ordinary surface. Only this catalogue still shows it. Stored module data is kept.',

  'clinicModules.health': 'Configuration',
  'clinicModules.health.ready': 'Ready',
  'clinicModules.health.configuration_required': 'Configuration required',
  'clinicModules.health.unavailable': 'Cannot be checked',
  'clinicModules.health.unknown': 'Not reported',
  'clinicModules.health.unrecognized': 'Unrecognized status',
  'clinicModules.healthDetail.ready': 'The local configuration is complete.',
  'clinicModules.healthDetail.configuration_required':
    'Complete the settings required for the operation you want to perform. Enabling the module does not fill in its settings.',
  'clinicModules.healthDetail.unavailable':
    'The local configuration status could not be determined. Nothing outside this installation was contacted to find out.',
  'clinicModules.healthDetail.unknown': 'The module reports no configuration status.',
  'clinicModules.healthDetail.unrecognized':
    'The server reported a configuration status this version of the workspace does not know. The state shown is still the server’s.',
  'clinicModules.healthIssues': 'Reported issues',

  'clinicModules.switchEnabled': 'Enabled',
  'clinicModules.switchHidden': 'Hidden',
  'clinicModules.switchOn': 'On',
  'clinicModules.switchOff': 'Off',
  'clinicModules.switchEnabledOn': 'Enabled: on',
  'clinicModules.switchEnabledOff': 'Enabled: off',
  'clinicModules.switchHiddenOn': 'Hidden: on',
  'clinicModules.switchHiddenOff': 'Hidden: off',
  'clinicModules.action.enable': 'Turn on',
  'clinicModules.action.disable': 'Turn off',
  'clinicModules.action.hide': 'Hide',
  'clinicModules.action.unhide': 'Show again',
  'clinicModules.switchEnabledHint':
    'On: the module runs. Off: stored data stays readable and every new effect stops.',
  'clinicModules.switchHiddenHint':
    'On: also turns the module off and removes it from every ordinary surface. Off: the module is shown again but stays off.',
  'clinicModules.enableUnavailable':
    'A hidden module cannot be turned on. Turn Hidden off first; it stays off until turned on in a separate step.',
  'clinicModules.pending': 'Saving the change…',
  'clinicModules.saved': 'Change saved. The server confirmed the new state (revision {revision}).',
  'clinicModules.savedListStale':
    'Change saved and confirmed by the server (revision {revision}). The rest of the list could not be refreshed and may be out of date.',
  'clinicModules.stateChangedElsewhere':
    'Someone else changed this module while you were deciding. Nothing was changed by you; the state shown is the server’s current one. Choose again if it still needs changing.',

  'clinicModules.confirm.enableTitle': 'Turn on {name}?',
  'clinicModules.confirm.enableDescription':
    'The module starts accepting inbound requests, running commands and background work, and making the outbound calls it declares. It gains no access that an account does not already hold.',
  'clinicModules.confirm.enable': 'Turn on',
  'clinicModules.confirm.disableTitle': 'Turn off {name}?',
  'clinicModules.confirm.disableDescription':
    'Stored module data stays readable and nothing is deleted. New inbound requests, commands, background jobs, AI calls, messages and every other effect the module starts are refused until it is turned on again.',
  'clinicModules.confirm.disable': 'Turn off',
  'clinicModules.confirm.hideTitle': 'Hide {name}?',
  'clinicModules.confirm.hideDescription':
    'Hiding also turns the module off in the same step. Its navigation entries, Dashboard blocks, patient badges, sources, chart sections and timeline entries disappear from every ordinary surface.',
  'clinicModules.confirm.hideRetained':
    'Stored module data is kept. Clinical records, patient messages and audit entries the clinic already recorded stay exactly as they are. Only developers can see it in this catalogue and make it visible again.',
  'clinicModules.confirm.hide': 'Hide and turn off',
  'clinicModules.confirm.unhideTitle': 'Show {name} again?',
  'clinicModules.confirm.unhideDescription':
    'The module returns to the ordinary surfaces but stays off. Turning it on is a separate step, taken afterwards.',
  'clinicModules.confirm.unhide': 'Show again',
  'clinicModules.confirm.cancel': 'Keep as it is',
  'clinicModules.confirm.retry': 'Try again',

  'clinicModules.reason.legend': 'Reason',
  'clinicModules.reason.hint': 'Recorded with the change in the clinic’s audit trail.',
  'clinicModules.reason.other': 'Other',
  'clinicModules.reason.detail': 'Reason details',
  'clinicModules.reason.rollout': 'Rollout in this clinic',
  'clinicModules.reason.maintenance': 'Maintenance',
  'clinicModules.reason.maintenanceDone': 'Maintenance finished',
  'clinicModules.reason.configuration': 'Configuration not complete',
  'clinicModules.reason.notUsed': 'Not used in this clinic',
  'clinicModules.reason.postponed': 'Rollout postponed',

  'clinicModules.error.concurrent':
    'Someone else changed this module first. Nothing was changed by you; the current state is now shown.',
  'clinicModules.error.hiddenCannotEnable':
    'The module is hidden and cannot be turned on. Nothing was changed; turn Hidden off first.',
  'clinicModules.error.forbidden': 'This account may no longer change module state. Nothing was changed.',
  'clinicModules.error.invalid': 'The server refused the change as invalid. Nothing was changed.',
  'clinicModules.error.notFound': 'This module is unavailable for this organization. Nothing was changed.',
  'clinicModules.error.unavailable':
    'The server could not be reached. Nothing was changed; try again.',
  'clinicModules.error.malformed':
    'The server answered, but its answer could not be read. Reload the list to see whether the change was applied.',
  'clinicModules.error.failed': 'The change could not be saved. Nothing was changed.',
} as const
